<template>
  <div class="sc-wrap" :style="{ width: size + 'px', height: size + 'px' }">
    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
      <!-- background ring -->
      <circle
        :cx="size / 2" :cy="size / 2" :r="radius"
        fill="none" stroke="#e5e7eb" :stroke-width="stroke"
      />
      <!-- progress ring -->
      <circle
        v-if="percent !== null && percent !== undefined"
        :cx="size / 2" :cy="size / 2" :r="radius"
        fill="none" :stroke="ringColor" :stroke-width="stroke"
        stroke-linecap="round"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashOffset"
        :transform="`rotate(-90 ${size / 2} ${size / 2})`"
      />
    </svg>
    <span class="sc-label" :style="{ color: ringColor, fontSize: labelSize + 'px' }">
      <template v-if="percent !== null && percent !== undefined">{{ Math.round(percent) }}%</template>
      <template v-else>—</template>
    </span>
  </div>
</template>

<script>
/**
 * Circular score indicator (0-100). Renders an SVG ring + the percent in the
 * middle. Colour shifts by threshold: green ≥ 90, amber ≥ 70, red below.
 *
 * Props:
 *   - percent  number|null  0..100 (null/undefined renders an empty ring + "—")
 *   - size     px diameter (default 56)
 */
export default {
  name: 'ScoreCircle',
  props: {
    percent: { type: Number, default: null },
    size:    { type: Number, default: 56 },
    // Multiplier for the centered percent label (1 = default sizing).
    labelScale: { type: Number, default: 1 },
    // Optional explicit ring thickness in px (default scales with size).
    strokeWidth: { type: Number, default: null },
  },
  computed: {
    stroke() {
      if (this.strokeWidth) return this.strokeWidth;
      return Math.max(4, Math.round(this.size * 0.11));
    },
    radius()        { return (this.size - this.stroke) / 2; },
    circumference() { return 2 * Math.PI * this.radius; },
    clampedPercent() {
      if (this.percent === null || this.percent === undefined) return 0;
      return Math.max(0, Math.min(100, this.percent));
    },
    dashOffset() {
      return this.circumference * (1 - this.clampedPercent / 100);
    },
    ringColor() {
      const p = this.clampedPercent;
      if (p >= 90) return '#16a34a'; // green
      if (p >= 70) return '#d97706'; // amber
      return '#dc2626';                // red
    },
    labelSize() {
      return Math.max(11, Math.round(this.size * 0.28)) * this.labelScale;
    },
  },
};
</script>

<style scoped>
.sc-wrap {
  position: relative;
  display: inline-block;
}
.sc-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  letter-spacing: -0.02em;
}
</style>
